/**
 * Transaction Filters Component
 * 
 * Filters for transaction list
 */

import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Search, X, Calendar } from 'lucide-react'
import { ThaiDatePicker } from '@/components/ui/date-time-picker'
import { toDateInputValue } from '@/lib/date-format'

// ============================================
// Types
// ============================================

export interface TransactionFilters {
  searchText: string
  type: string // '' | 'Deposit' | 'Withdraw'
  channel: string // '' | 'Panel' | 'Member' | 'Transfer'
  bankType: string // '' | 'bank' | 'bonus'
  status?: string // '' | 'Pending' | 'Processing' | 'Approved' | 'Rejected' | 'Completed' | 'Cancelled' (only for history)
  dateFrom?: string
  dateTo?: string
}

interface TransactionFiltersProps {
  filters: TransactionFilters
  onChange: (filters: TransactionFilters) => void
  showStatusFilter?: boolean
  showDateFilter?: boolean
}

// ============================================
// Main Component
// ============================================

export function TransactionFiltersSection({
  filters,
  onChange,
  showStatusFilter = false,
  showDateFilter = false,
}: TransactionFiltersProps) {
  const updateFilter = (key: keyof TransactionFilters, value: string) => {
    onChange({ ...filters, [key]: value === '__all__' ? '' : value })
  }

  const clearFilters = () => {
    onChange({
      searchText: '',
      type: '',
      channel: '',
      bankType: '',
      status: '',
      dateFrom: '',
      dateTo: '',
    })
  }

  const hasFilters = filters.searchText || filters.type || filters.channel || 
    filters.bankType || filters.status || filters.dateFrom || filters.dateTo

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={filters.searchText}
              onChange={(e) => updateFilter('searchText', e.target.value)}
              placeholder="ค้นหา ยูสเซอร์, ชื่อ นามสกุล, เบอร์โทรศัพท์, เลขที่บัญชี..."
              className="pl-9"
            />
          </div>
          
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Channel */}
            <Select 
              value={filters.channel || '__all__'} 
              onValueChange={(v) => updateFilter('channel', v)}
            >
              <SelectTrigger className="w-[120px]">
                <SelectValue placeholder="ช่องทาง" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">ทุกช่องทาง</SelectItem>
                <SelectItem value="Panel">Panel</SelectItem>
                <SelectItem value="Member">ลูกค้า</SelectItem>
                <SelectItem value="Transfer">โอน</SelectItem>
              </SelectContent>
            </Select>
            
            {/* Type */}
            <Select 
              value={filters.type || '__all__'} 
              onValueChange={(v) => updateFilter('type', v)}
            >
              <SelectTrigger className="w-[100px]">
                <SelectValue placeholder="ประเภท" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">ฝาก-ถอน</SelectItem>
                <SelectItem value="Deposit">ฝาก</SelectItem>
                <SelectItem value="Withdraw">ถอน</SelectItem>
              </SelectContent>
            </Select>
            
            {/* Bank Type */}
            <Select 
              value={filters.bankType || '__all__'} 
              onValueChange={(v) => updateFilter('bankType', v)}
            >
              <SelectTrigger className="w-[120px]">
                <SelectValue placeholder="ประเภทบัญชี" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">ทั้งหมด</SelectItem>
                <SelectItem value="bank">ธนาคาร</SelectItem>
                <SelectItem value="bonus">โบนัส</SelectItem>
              </SelectContent>
            </Select>
            
            {/* Status (only for history) */}
            {showStatusFilter && (
              <Select 
                value={filters.status || '__all__'} 
                onValueChange={(v) => updateFilter('status', v)}
              >
                <SelectTrigger className="w-[130px]">
                  <SelectValue placeholder="สถานะ" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">ทุกสถานะ</SelectItem>
                  <SelectItem value="Pending">รอดำเนินการ</SelectItem>
                  <SelectItem value="Approved">สำเร็จ</SelectItem>
                  <SelectItem value="Rejected">ปฏิเสธ</SelectItem>
                </SelectContent>
              </Select>
            )}
            
            {/* Date Range (only for history) */}
            {showDateFilter && (
              <>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <div className="w-[180px]">
                    <ThaiDatePicker
                      selected={filters.dateFrom ? new Date(filters.dateFrom) : null}
                      onChange={(date) => updateFilter('dateFrom', date ? toDateInputValue(date) : '')}
                      placeholder="วันที่เริ่มต้น"
                    />
                  </div>
                  <span className="text-muted-foreground">-</span>
                  <div className="w-[180px]">
                    <ThaiDatePicker
                      selected={filters.dateTo ? new Date(filters.dateTo) : null}
                      onChange={(date) => updateFilter('dateTo', date ? toDateInputValue(date) : '')}
                      placeholder="วันที่สิ้นสุด"
                    />
                  </div>
                </div>
              </>
            )}
            
            {/* Clear Filters */}
            {hasFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="w-4 h-4 mr-1" />
                ล้าง
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}













